/**
 * Owns the Twilio access-token lifecycle for a Conversations client:
 *
 * - proactive refresh on `tokenAboutToExpire` (with retry + backoff),
 * - full client re-initialization once the token expires or the connection
 *   is denied (an expired twilsock session cannot be revived in place),
 * - a visibility check so sessions survive background-tab timer throttling,
 * - capped-backoff retries when re-initialization itself fails.
 *
 * All side effects (HTTP, SDK client creation, timers, DOM visibility) are
 * injected, so the logic is testable without the Twilio SDK or a browser.
 */

export interface LifecycleClient {
  on(
    event: 'connectionStateChanged' | 'stateChanged',
    listener: (state: string) => void,
  ): void;
  on(event: 'tokenAboutToExpire' | 'tokenExpired', listener: () => void): void;
  /**
   * Resolves through the SDK's `Client._conversationsPromise`. Asking for it is
   * what proves the client is usable at all — and what gives that promise an
   * observer (see `attachClient`).
   */
  getSubscribedConversations(): Promise<unknown>;
  updateToken(token: string): Promise<unknown>;
  removeAllListeners(): void;
  shutdown(): Promise<void>;
  connectionState: string;
}

export interface TwilioTokenLifecycleDeps<TClient extends LifecycleClient> {
  /** Calls `onVisible` whenever the page becomes visible; returns unsubscribe. */
  subscribeVisibility(onVisible: () => void): () => void;
  setTimer(fn: () => void, ms: number): unknown;
  /** Returns the `exp` claim (epoch seconds) of the token, or null. */
  decodeTokenExp(token: string): null | number;
  log(message: string, error?: unknown): void;
  createClient(token: string): TClient;
  /**
   * The current client was torn down to be replaced. Every SDK object obtained
   * from it (`Conversation`, `Message`) is dead from now on; whoever caches
   * them must drop them. Not called for `shutdown()` — its caller knows.
   */
  onClientDiscarded(): void;
  onStatusChange(status: string): void;
  /**
   * The rebuilds keep failing — reported once per streak, so an outage the
   * lifecycle cannot heal is visible somewhere other than the console.
   */
  reportPersistentFailure(error: unknown): void;
  /** Fetches a fresh access token from the backend. */
  fetchToken(): Promise<string>;
  clearTimer(id: unknown): void;
  now(): number;
}

export interface TwilioTokenLifecycle<TClient extends LifecycleClient> {
  /**
   * Tells the lifecycle that a real chat operation failed, so it can judge
   * whether the transport underneath is still alive. Safe to call with
   * anything — only a delivery failure rebuilds the client.
   *
   * Returns whether the lifecycle OWNS the failure: it is (now) recovering the
   * client the operation ran on, so the error is a symptom of an outage that is
   * already being handled rather than news for the caller to report.
   */
  reportOperationFailure(error: unknown): boolean;
  getClient(): TClient | null;
  shutdown(): Promise<void>;
  start(): Promise<void>;
}

// Errors that mean the request NEVER REACHED the service. `Transport failure:
// <status>` and `Bad Request` are the opposite — the socket delivered and the
// service answered — and `cancelled by user` is our own teardown; rebuilding on
// any of those would turn every rejected operation into a reconnect.
const DEAD_TRANSPORT_MESSAGES = ['Twilsock: request timeout', 'disconnected'];

// twilsock's `TransportUnavailableError`: the transport is in a TERMINAL state
// and rejects every send without trying ("Unable to connect: <termination
// reason>", the reason defaulting to "Connection is not initialized"). Only the
// current client's transport is worth rebuilding over — see
// `reportOperationFailure`.
const TERMINAL_TRANSPORT_PREFIX = 'Unable to connect: ';

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : '';

function isDeadTransportError(error: unknown) {
  const message = errorMessage(error);
  return DEAD_TRANSPORT_MESSAGES.some((known) => message.startsWith(known));
}

function isTerminalTransportError(error: unknown) {
  return errorMessage(error).startsWith(TERMINAL_TRANSPORT_PREFIX);
}

const FETCH_RETRY_ATTEMPTS = 3;
const FETCH_RETRY_BASE_DELAY_MS = 2_000;
const REINIT_BASE_DELAY_MS = 5_000;
const REINIT_MAX_DELAY_MS = 60_000;
// Consecutive rebuilds without a usable client before the outage is reported:
// the third one is due ~35 s into the failure (5 s + 10 s + 20 s of backoff),
// long past anything a single dropped socket explains.
const PERSISTENT_FAILURE_ATTEMPTS = 3;
// The Twilio chat token has a lifetime of its own, unrelated to the app session:
// refresh when the tab comes back with less than this much of it left.
// (Unrelated to the auth session cookie, which has no client-side timer since PR-2.)
const VISIBILITY_REFRESH_BUFFER_MS = 5 * 60 * 1_000;

export function createTwilioTokenLifecycle<TClient extends LifecycleClient>(
  deps: TwilioTokenLifecycleDeps<TClient>,
): TwilioTokenLifecycle<TClient> {
  let client: TClient | null = null;
  let tokenExpiresAtMs: null | number = null;
  let isShutDown = false;
  let currentOp: 'refresh' | 'reinit' | null = null;
  let reinitRequestedDuringRefresh = false;
  let reinitRetryAttempts = 0;
  // The SDK announced `stateChanged: 'failed'` (or the readiness probe failed)
  // for the CURRENT client: every call on it is a symptom from now on.
  let clientFailed = false;
  let scheduledReinitTimer: unknown = null;
  let unsubscribeVisibility: (() => void) | null = null;
  const pendingTimers = new Set<unknown>();

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      const id = deps.setTimer(() => {
        pendingTimers.delete(id);
        resolve();
      }, ms);
      pendingTimers.add(id);
    });

  async function fetchTokenWithRetry() {
    let lastError: unknown;
    for (let attempt = 0; attempt < FETCH_RETRY_ATTEMPTS; attempt++) {
      if (isShutDown) throw new Error('Twilio lifecycle has been shut down');
      try {
        return await deps.fetchToken();
      } catch (error) {
        lastError = error;
        deps.log(
          `Twilio token fetch failed (attempt ${attempt + 1}/${FETCH_RETRY_ATTEMPTS})`,
          error,
        );
        if (attempt < FETCH_RETRY_ATTEMPTS - 1) {
          await sleep(FETCH_RETRY_BASE_DELAY_MS * 2 ** attempt);
        }
      }
    }
    throw lastError;
  }

  function rememberExpiration(token: string) {
    const exp = deps.decodeTokenExp(token);
    tokenExpiresAtMs = exp === null ? null : exp * 1_000;
  }

  // `connectionState` only describes the twilsock socket. A client can sit at
  // `connected` while its conversation set never arrived — `_initialize` does
  // not await that fetch — and every thread open and unread count then fails
  // against the rejected promise the SDK keeps on the instance. So ask for the
  // set once, up front: the answer decides whether this client is alive, and
  // the question itself is what gives the SDK's promise an observer instead of
  // letting it surface as an unhandled rejection (Sentry MYFITWORLD-V2-6B).
  // Deliberately not awaited by `start()` — a stillborn client must not hold
  // the chat UI for the 30 s twilsock takes to time out.
  function probeReadiness(created: TClient) {
    created.getSubscribedConversations().then(
      () => {
        if (isShutDown || client !== created) return;
        // only a client proven usable clears the backoff
        reinitRetryAttempts = 0;
      },
      (error: unknown) => {
        if (isShutDown || client !== created) return;
        clientFailed = true;
        deps.log('Twilio conversation set failed to load; rebuilding', error);
        scheduleReinit(error);
      },
    );
  }

  function attachClient(token: string) {
    const created = deps.createClient(token);
    client = created;
    clientFailed = false;
    rememberExpiration(token);

    created.on('connectionStateChanged', (state) => {
      if (isShutDown || client !== created) return;
      deps.onStatusChange(state);
      if (state === 'denied') requestReinit();
    });
    // The SDK's own verdict on initialization. Until now nothing listened, so a
    // client whose `_initialize` threw stayed installed forever.
    created.on('stateChanged', (state) => {
      if (isShutDown || client !== created) return;
      if (state !== 'failed') return;
      clientFailed = true;
      scheduleReinit(new Error('Twilio client initialization failed'));
    });
    created.on('tokenAboutToExpire', () => {
      if (isShutDown || client !== created) return;
      refreshToken();
    });
    created.on('tokenExpired', () => {
      if (isShutDown || client !== created) return;
      requestReinit();
    });

    probeReadiness(created);
  }

  async function refreshToken() {
    if (isShutDown || currentOp) return;
    currentOp = 'refresh';
    try {
      const token = await fetchTokenWithRetry();
      if (isShutDown || !client) return;
      await client.updateToken(token);
      rememberExpiration(token);
    } catch (error) {
      // A tokenExpired event (or the visibility check) triggers a full
      // re-initialization if the token actually runs out.
      deps.log('Twilio token refresh failed; will re-init on expiry', error);
    } finally {
      currentOp = null;
      if (reinitRequestedDuringRefresh) {
        reinitRequestedDuringRefresh = false;
        requestReinit();
      }
    }
  }

  // Every rebuild that is a REACTION TO FAILURE goes through here, so a Twilio
  // outage costs one attempt per capped-backoff step instead of a hot loop.
  //
  // At most one may be pending. A stillborn client announces itself twice — the
  // readiness probe rejects and the SDK emits `stateChanged: 'failed'` — and a
  // second rebuild queued off the same dead client comes due after the first has
  // already produced a working one, which it would then tear down.
  function scheduleReinit(cause: unknown) {
    if (isShutDown || scheduledReinitTimer !== null) return;
    const delay = Math.min(
      REINIT_BASE_DELAY_MS * 2 ** reinitRetryAttempts,
      REINIT_MAX_DELAY_MS,
    );
    reinitRetryAttempts += 1;
    if (reinitRetryAttempts === PERSISTENT_FAILURE_ATTEMPTS) {
      deps.reportPersistentFailure(cause);
    }
    const id = deps.setTimer(() => {
      scheduledReinitTimer = null;
      pendingTimers.delete(id);
      requestReinit();
    }, delay);
    scheduledReinitTimer = id;
    pendingTimers.add(id);
  }

  function cancelScheduledReinit() {
    if (scheduledReinitTimer === null) return;
    deps.clearTimer(scheduledReinitTimer);
    pendingTimers.delete(scheduledReinitTimer);
    scheduledReinitTimer = null;
  }

  function requestReinit() {
    if (isShutDown) return;
    // An immediate signal (token expiry, denied connection, visibility catch-up)
    // supersedes any backoff queued for the same dead client. Leaving that
    // timer alive would rebuild once now and then tear down the healthy
    // replacement when the stale timer fired.
    cancelScheduledReinit();
    if (currentOp === 'reinit') return;
    if (currentOp === 'refresh') {
      reinitRequestedDuringRefresh = true;
      return;
    }
    reinit();
  }

  // An expired twilsock session cannot be revived with updateToken — the
  // client must be torn down and rebuilt with a fresh token.
  async function reinit() {
    if (isShutDown || currentOp) return;
    currentOp = 'reinit';
    try {
      const old = client;
      client = null;
      clientFailed = false;
      tokenExpiresAtMs = null;
      if (old) {
        old.removeAllListeners();
        old.shutdown().catch(() => undefined);
        // Before the token fetch: the replacement may take seconds, and until
        // it connects the UI must neither believe it is connected nor serve
        // objects of the client that just died (MYFITWORLD-V2-7N).
        deps.onStatusChange('connecting');
        deps.onClientDiscarded();
      }
      const token = await fetchTokenWithRetry();
      if (isShutDown) return;
      attachClient(token);
    } catch (error) {
      deps.log('Twilio client re-initialization failed; retrying', error);
      scheduleReinit(error);
    } finally {
      currentOp = null;
    }
  }

  // Background tabs throttle timers, so tokenAboutToExpire may never fire
  // there; catch up as soon as the tab becomes visible again.
  function handleVisible() {
    if (isShutDown || !client || currentOp) return;
    if (tokenExpiresAtMs === null) return;
    const remainingMs = tokenExpiresAtMs - deps.now();
    if (remainingMs <= 0) {
      requestReinit();
    } else if (remainingMs <= VISIBILITY_REFRESH_BUFFER_MS) {
      refreshToken();
    }
  }

  return {
    getClient: () => client,

    // The transport has no heartbeat (no ping, no keepalive — in twilsock
    // 0.13.11 and in 1.0.1 alike), so a socket the browser still holds open but
    // the service has stopped answering reports `connected` right up until each
    // request hits its own 30 s timeout. `probeReadiness` only judges a client
    // at birth; after that the only evidence the transport died is a real
    // operation failing to be delivered. `api/twilio.ts` funnels every SDK call
    // through one error path, and that path reports here.
    //
    // A terminal transport is judged against the CURRENT client: one that still
    // reads `connected` cannot be the one whose transport ended, so the call
    // ran on an object of a client already replaced (V2-7N) — rebuilding the
    // healthy one would only tear it down for nothing.
    reportOperationFailure(error: unknown) {
      if (isShutDown) return false;
      if (
        client &&
        (isDeadTransportError(error) ||
          (isTerminalTransportError(error) &&
            client.connectionState !== 'connected'))
      ) {
        deps.log(
          'Twilio operation failed on a dead transport; rebuilding',
          error,
        );
        scheduleReinit(error);
      }
      // Owned while recovery is underway: a rebuild queued or running, or a
      // client the SDK itself declared failed (V2-80 — its accessors throw a
      // TypeError, not a transport error).
      return (
        clientFailed || scheduledReinitTimer !== null || currentOp === 'reinit'
      );
    },

    // Teardown is BEST-EFFORT and MUST NEVER REJECT. Every caller either drops
    // the promise or is itself a teardown path: `useTwilioClient`'s effect
    // cleanup calls `shutdownTwilioClient()` as a bare statement (React
    // cleanups are synchronous, so nothing observes it), and
    // `initializeTwilioService` awaits it only to get the old client out of the
    // way before building a new one. Tearing a socket down mid-handshake makes
    // twilsock reject — "Bad Request", "request timeout" — and unobserved,
    // that became three Sentry issues (MYFITWORLD-V2-6A / -68 / -6B, all
    // onunhandledrejection with no in-app frame). Nothing upstream can act on
    // the failure: the client, its listeners and the timers are already
    // released by the time the SDK is asked. So report it and finish.
    async shutdown() {
      if (isShutDown) return;
      isShutDown = true;
      unsubscribeVisibility?.();
      unsubscribeVisibility = null;
      for (const id of pendingTimers) deps.clearTimer(id);
      pendingTimers.clear();
      scheduledReinitTimer = null;
      const current = client;
      client = null;
      tokenExpiresAtMs = null;
      if (current) {
        current.removeAllListeners();
        try {
          await current.shutdown();
        } catch (error) {
          deps.log('Twilio client shutdown failed; continuing teardown', error);
        }
      }
    },

    async start() {
      if (isShutDown) throw new Error('Twilio lifecycle has been shut down');
      if (client) return;
      unsubscribeVisibility = deps.subscribeVisibility(handleVisible);
      const token = await fetchTokenWithRetry();
      if (isShutDown) throw new Error('Twilio lifecycle has been shut down');
      attachClient(token);
    },
  };
}
